import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import type { ProfilePersonaView } from '../../../api/PublicProfile/publicProfileApi';

interface DeletePersonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (personaId: number) => void;
  persona: ProfilePersonaView | null;
}

const DeletePersonaModal: React.FC<DeletePersonaModalProps> = ({ isOpen, onClose, onDelete, persona }) => {
  const { t } = useTranslation();

  const handleDelete = () => {
    if (persona?.id) {
      onDelete(persona.id);
      onClose();
    }
  };

  const hasProfiles = persona && persona.profileCount > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('Delete Persona')}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-foreground mb-2">
                {t('Are you sure you want to delete this persona?')}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t('You are about to delete the persona')} <span className="font-semibold">"{persona?.name}"</span>.
              </p>

              {hasProfiles ? (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-3">
                  <p className="text-sm text-destructive">
                    <AlertCircle className="w-4 h-4 inline me-1" />
                    {t('This persona has {{count}} profile(s) using it. You cannot delete a persona that is in use.', { count: persona?.profileCount })}
                  </p>
                </div>
              ) : (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                  <p className="text-sm text-warning">
                    <AlertCircle className="w-4 h-4 inline me-1" />
                    {t('All settings linked to this persona across all tabs will be affected. This action cannot be undone.')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!!hasProfiles}
          >
            {t('Delete Persona')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeletePersonaModal;
